import { auth } from './firebase-config';

const API_BASE_URL = '/v1';

const getAuthToken = async (): Promise<string> => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }
    return await user.getIdToken();
};

export interface GiftPointsRequest {
    targetUserId: string;
    points: number;
    reason: string;
}

export interface GiftPointsResponse {
    targetUserId: string;
    pointsGifted: number;
    reason: string;
    newBalance: number;
    transactionId: string;
}

export interface UserBalanceResponse {
    userId: string;
    balance: number;
}

export interface UserProfileResponse {
    userId: string;
    email: string;
    profile: any;
    balance: number;
}

export interface DeleteUserRequest {
    reason: string;
}

export interface DeleteUserResponse {
    deletedUserId: string;
    adminUserId: string;
    reason: string;
    userEmail: string;
}

export interface BatchSearchRequest {
    ambassadorStatus?: boolean | null;
    premiumStatus?: boolean | null;
    postcodePrefix?: string;
}

export interface BatchSearchUser {
    userId: string;
    email: string;
    profile: any;
}

export interface BatchSearchResponse {
    users: BatchSearchUser[];
    count: number;
    limit: number;
}

export const giftPoints = async (request: GiftPointsRequest): Promise<GiftPointsResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/user-admin/gift-points`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to gift points');
    }

    const data = (await response.json())['data'];
    return data;
};

export const getUserBalance = async (userId: string): Promise<UserBalanceResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/user-admin/user-balance/${userId}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get user balance');
    }

    const data = (await response.json())['data'];
    return data;
};

export const getUserProfile = async (userIdOrEmail: string): Promise<UserProfileResponse> => {
    const token = await getAuthToken();
    // Encode the identifier in case it's an email with special characters
    const encodedIdentifier = encodeURIComponent(userIdOrEmail);
    const response = await fetch(`${API_BASE_URL}/user-admin/user-profile/${encodedIdentifier}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get user profile');
    }

    const data = (await response.json())['data'];
    return data;
};

export const batchSearchUsers = async (request: BatchSearchRequest): Promise<BatchSearchResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/user-admin/batch-search`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to perform batch search');
    }

    const data = (await response.json())['data'];
    return data;
};

export const deleteUser = async (userId: string, request: DeleteUserRequest): Promise<DeleteUserResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/user-admin/user/${userId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
    }

    const data = (await response.json())['data'];
    return data;
};

// Task management API functions
export const getUserTasks = async (userId: string): Promise<any> => {

    if (userId === 'test') {
        return {
            items: [
                {extraTasks: [], state: "collected", layer: "primary", type: "walking_task", data: {target: 6000}, instanceId: "0163d314-ebe0-4a2d-89b8-243c39c40e1e", streakDays: 0, expires: "2025-04-10T22:00:42.000Z", streakMultiplier: 0, created: "2025-04-10T13:50:42.000Z", activationAmount: {points: 10}, completionAmount: {points: 100}}
            ]
        }
    }
    
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('No authentication token');

    const response = await fetch(`${API_BASE_URL}/tasks/admin/user/${userId}?ts=${Date.now()}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.statusText}`);
    }

    const data = (await response.json())['data'];
    return data;
};

export const updateTask = async (userId: string, taskId: string, updates: {
    created?: string;
    expires?: string;
    type?: string;
    state?: string;
    targetSteps?: number;
    streakDays?: number;
    streakMultiplier?: number;
    extraTasks?: any[];
}): Promise<any> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('No authentication token');

    const response = await fetch(`${API_BASE_URL}/tasks/admin/${taskId}`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            userId,
            ...updates
        }),
    });

    if (!response.ok) {
        throw new Error(`Failed to update task: ${response.statusText}`);
    }

    const data = (await response.json())['data'];
    return data;
};

export const deleteTask = async (userId: string, taskId: string): Promise<any> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('No authentication token');

    const response = await fetch(`${API_BASE_URL}/tasks/admin/${taskId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
        throw new Error(`Failed to delete task: ${response.statusText}`);
    }

    const data = (await response.json())['data'];
    return data;
};
